import { useEffect, useRef } from 'react';

/** How often a visible page re-reads data that someone else may have changed. */
export const DEFAULT_REFRESH_MS = 15_000;

/**
 * Keep a screen current without a live channel of its own.
 *
 * Several views load once on mount and then show whatever was true at that
 * moment — a teammate's answer, a coordinator's grade, a team that has just
 * formed. This re-runs `refresh` on an interval while the page is visible, and
 * immediately when it returns to the foreground.
 *
 * The foreground trigger is the one that matters on a rally: a phone spends
 * most of a station in someone's pocket, so "when the screen comes back" is
 * when a stale screen is actually being read.
 *
 * Nothing runs while the page is hidden — a pocketed phone must not keep
 * polling. `refresh` should be quiet (no loading spinner), or the view will
 * blink on every tick.
 */
export function useAutoRefresh(refresh: () => void | Promise<void>, intervalMs = DEFAULT_REFRESH_MS) {
  // Held in a ref so a new callback identity does not restart the interval —
  // callers usually pass a useCallback that changes with their params.
  const latest = useRef(refresh);
  useEffect(() => {
    latest.current = refresh;
  }, [refresh]);

  useEffect(() => {
    const run = () => {
      if (document.visibilityState === 'visible') void latest.current();
    };
    const id = window.setInterval(run, intervalMs);
    document.addEventListener('visibilitychange', run);
    window.addEventListener('focus', run);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', run);
      window.removeEventListener('focus', run);
    };
  }, [intervalMs]);
}
